import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Check, Clock, Circle, TrendingUp, Loader2 } from 'lucide-react';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const NOW_MONTH = new Date().getMonth(); // 0-indexed

const FALLBACK_PHASES = [
  { id: '1', name: 'Fase 1', subtitle: 'Fondazione', period: 'Gen — Mar', months: [0, 1, 2], progress: 85, milestones: [
    { text: 'Piattaforma operativa live', status: 'completed' },
    { text: '15 agenti configurati', status: 'completed' },
    { text: 'Integrazioni Buffer+Beehiiv+Resend', status: 'completed' },
    { text: '6 eventi completati', status: 'completed' },
    { text: '200 iscritti newsletter', status: 'inProgress' },
  ]},
  { id: '2', name: 'Fase 2', subtitle: 'Crescita', period: 'Apr — Giu', months: [3, 4, 5], progress: 15, milestones: [
    { text: 'Piano editoriale attivo', status: 'inProgress' },
    { text: '500 contatti qualificati', status: 'inProgress' },
    { text: '10 partnership attive', status: 'inProgress' },
    { text: '3 workshop a pagamento', status: 'future' },
    { text: 'Primo prodotto formativo', status: 'future' },
  ]},
  { id: '3', name: 'Fase 3', subtitle: 'Scaling', period: 'Lug — Set', months: [6, 7, 8], progress: 0, milestones: [
    { text: 'Segmentazione verticale', status: 'future' },
    { text: 'Referral program', status: 'future' },
    { text: 'Espansione Svizzera tedesca', status: 'future' },
    { text: 'Events-as-a-Service', status: 'future' },
  ]},
  { id: '4', name: 'Fase 4', subtitle: 'Maturita', period: 'Ott — Dic', months: [9, 10, 11], progress: 0, milestones: [
    { text: 'Prodotto formativo scalabile', status: 'future' },
    { text: 'Network auto-generativo', status: 'future' },
    { text: 'Revenue target CHF 8K/mese', status: 'future' },
  ]},
];

const FALLBACK_TARGETS = [
  { title: '200 iscritti newsletter', current: 142, target: 200, unlocks: 'Content marketing come canale primario', estimate: '~4 settimane', velocity: '+12/settimana' },
  { title: '20 eventi completati', current: 6, target: 20, unlocks: 'Social proof corporate', estimate: '~6 mesi', velocity: '+1.5/mese' },
  { title: '10 partnership attive', current: 2, target: 10, unlocks: 'Network effect tra partner', estimate: '~4 mesi', velocity: '+2/mese' },
];

function parsePhases(apiData) {
  if (!apiData?.phases?.length) return FALLBACK_PHASES;
  return apiData.phases.map((p, i) => ({
    id: String(i + 1),
    name: p.name || `Fase ${i + 1}`,
    subtitle: p.subtitle || '',
    period: p.period || '',
    months: p.months || [i * 3, i * 3 + 1, i * 3 + 2],
    progress: p.progress ?? 0,
    milestones: (p.milestones || p.kpis || []).map(m => ({
      text: m.text || m.name || m.label || '',
      status: m.done ? 'completed' : m.in_progress ? 'inProgress' : 'future',
    })),
  }));
}

function parseTargets(apiData) {
  if (!apiData?.next_milestones?.length && !apiData?.targets?.length) return FALLBACK_TARGETS;
  return (apiData.next_milestones || apiData.targets || []).map(t => ({
    title: t.title || t.name || '',
    current: t.current ?? 0,
    target: t.target ?? 100,
    unlocks: t.unlocks || '',
    estimate: t.estimate || '',
    velocity: t.velocity || '',
  }));
}

function getPhaseStyle(phase) {
  const isActive = phase.months.includes(NOW_MONTH);
  const isCompleted = phase.months[phase.months.length - 1] < NOW_MONTH;
  if (isCompleted || (isActive && phase.progress > 0)) {
    return { backgroundColor: '#2563EB', opacity: isActive ? 1 : 0.8 };
  }
  if (isActive) {
    return { backgroundColor: '#2563EB', backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.2) 5px, rgba(255,255,255,0.2) 10px)' };
  }
  return { backgroundColor: '#E5E7EB', opacity: 0.6 };
}

export default function TimelinePage() {
  const [phases, setPhases] = useState(FALLBACK_PHASES);
  const [targets, setTargets] = useState(FALLBACK_TARGETS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statusRes, roadmapRes] = await Promise.allSettled([
          api.get('/phase/status'),
          api.get('/phase/roadmap'),
        ]);
        if (statusRes.status === 'fulfilled') {
          const p = parsePhases(statusRes.value.data);
          if (p.length) setPhases(p);
          const t = parseTargets(statusRes.value.data);
          if (t.length) setTargets(t);
        }
        if (roadmapRes.status === 'fulfilled' && roadmapRes.value.data?.phases?.length) {
          setPhases(parsePhases(roadmapRes.value.data));
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <div className="flex h-full items-center justify-center" data-testid="timeline-loading">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#2563EB' }} />
    </div>
  );

  const activePhase = phases.find(p => p.months.includes(NOW_MONTH));

  return (
    <div className="h-full overflow-y-auto p-6" data-testid="timeline-page" style={{ backgroundColor: '#FAFBFC' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="mb-2 text-[20px] font-bold" style={{ color: '#1B2B4B' }}>
          Progetto HumanAImpact — Piano Anno 1
        </h1>
        <p className="text-[13px]" style={{ color: '#7C8CA2' }}>
          Gennaio 2026 &rarr; Dicembre 2026 &middot;{' '}
          {activePhase && <>{activePhase.name} al {activePhase.progress}% &middot; </>}
          {phases.filter(p => p.progress > 0 && p.progress < 100).length} fasi in corso
        </p>
      </div>

      {/* Timeline Gantt */}
      <div className="mb-8">
        <div className="relative mb-2 h-[50px]">
          {phases.map(phase => {
            const startPct = (phase.months[0] / 12) * 100;
            const widthPct = (phase.months.length / 12) * 100;
            return (
              <div key={phase.id}
                className="absolute top-0 flex h-full cursor-pointer items-center justify-center rounded-lg transition-all"
                style={{ left: `${startPct}%`, width: `calc(${widthPct}% - 4px)`, ...getPhaseStyle(phase) }}
                data-testid={`gantt-phase-${phase.id}`}>
                <span className="text-[12px] font-semibold text-white">
                  {phase.name} {phase.progress > 0 && `(${phase.progress}%)`}
                </span>
              </div>
            );
          })}
          {/* Today */}
          <div className="absolute top-0 h-full w-[2px]"
            style={{ left: `${((NOW_MONTH + 0.2) / 12) * 100}%`, background: 'repeating-linear-gradient(to bottom, #DC2626 0, #DC2626 5px, transparent 5px, transparent 10px)' }}>
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 text-[9px] font-bold text-white"
              style={{ backgroundColor: '#DC2626' }}>OGGI</span>
          </div>
        </div>
        <div className="flex">
          {MONTHS.map((m, i) => (
            <div key={m} className="flex-1 text-center text-[11px]"
              style={{ color: i === NOW_MONTH ? '#DC2626' : '#9CA3AF', fontWeight: i === NOW_MONTH ? 600 : 400 }}>
              {m}
            </div>
          ))}
        </div>
      </div>

      {/* Phase cards */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        {phases.map(phase => {
          const isActive = phase.months.includes(NOW_MONTH);
          const isFuture = phase.months[0] > NOW_MONTH;
          return (
            <div key={phase.id} className="rounded-xl p-4 transition-all"
              style={{ border: `${isActive ? '2px' : '1px'} solid ${isActive ? '#2563EB' : '#E5E7EB'}`, opacity: isFuture ? 0.5 : 1 }}
              data-testid={`phase-card-${phase.id}`}>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-[14px] font-bold" style={{ color: '#1B2B4B' }}>{phase.name} — {phase.subtitle}</h3>
                  <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{phase.period}</p>
                </div>
                {phase.progress > 0 && (
                  <span className="text-[20px] font-bold" style={{ color: phase.progress >= 80 ? '#16A34A' : '#2563EB' }}>
                    {phase.progress}%
                  </span>
                )}
              </div>
              {phase.progress > 0 && (
                <div className="mb-3 h-2 overflow-hidden rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${phase.progress}%`, backgroundColor: phase.progress >= 80 ? '#16A34A' : '#2563EB' }} />
                </div>
              )}
              <div className="space-y-1.5">
                {phase.milestones.map((m, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    {m.status === 'completed' ? <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                      : m.status === 'inProgress' ? <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                      : <Circle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" style={{ color: '#D1D5DB' }} />}
                    <span className="text-[12px]" style={{ color: m.status === 'completed' ? '#16A34A' : m.status === 'inProgress' ? '#F59E0B' : '#9CA3AF' }}>
                      {m.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Targets */}
      <div>
        <h2 className="mb-4 text-[12px] font-semibold uppercase tracking-wider" style={{ color: '#7C8CA2' }}>
          Prossimi traguardi
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {targets.map((t, idx) => {
            const pct = Math.round((t.current / t.target) * 100);
            return (
              <div key={idx} className="rounded-xl p-4" style={{ border: '1px solid #E5E7EB' }} data-testid={`target-${idx}`}>
                <h3 className="mb-2 text-[14px] font-bold" style={{ color: '#1B2B4B' }}>{t.title}</h3>
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#16A34A' : '#2563EB' }} />
                  </div>
                  <span className="text-[13px] font-semibold" style={{ color: '#1B2B4B' }}>{t.current}/{t.target} ({pct}%)</span>
                </div>
                <div className="space-y-1">
                  {t.unlocks && <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-medium" style={{ color: '#16A34A' }}>Sblocca:</span>
                    <span className="text-[12px]" style={{ color: '#374151' }}>{t.unlocks}</span>
                  </div>}
                  {t.estimate && <div className="flex items-center gap-1.5">
                    <span className="text-[12px]" style={{ color: '#7C8CA2' }}>Stima:</span>
                    <span className="text-[12px]" style={{ color: '#374151' }}>{t.estimate}</span>
                  </div>}
                  {t.velocity && <div className="flex items-center gap-1.5">
                    <TrendingUp className="h-3 w-3" style={{ color: '#16A34A' }} />
                    <span className="text-[12px]" style={{ color: '#374151' }}>{t.velocity}</span>
                  </div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
